/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
import { FormattedMessage, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { LinkButton } from '@atlaskit/button/new';
import { cssMap, cx, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import Image from '@atlaskit/image';
import Link from '@atlaskit/link';
import { Box, Text } from '@atlaskit/primitives/compiled';
import Toggle from '@atlaskit/toggle';
import { token, useThemeObserver } from '@atlaskit/tokens';

import teamworkCollectionHeaderDark from '../../../assets/teamwork_collection_header.dark.png';
import teamworkCollectionHeaderLight from '../../../assets/teamwork_collection_header.light.png';
import { useModalContext } from '../../../contexts/modal-context';
import type { NormalizeSeatScreenProps } from '../index';
import { normalizeSeatModalMessages } from '../messages';

const AdministrationLink = ({ orgId }: { orgId?: string }) => {
	const href = orgId ? `https://admin.atlassian.com/o/${orgId}/users` : '/admin';

	return (
		<Link href={href} target="_blank" rel="noopener noreferrer">
			<FormattedMessage {...normalizeSeatModalMessages.administrationLink} />
		</Link>
	);
};

const styles = cssMap({
	content: {
		display: 'flex',
		height: '100%',
		paddingTop: token('space.600'),
		paddingRight: token('space.600'),
		paddingBottom: token('space.600'),
		paddingLeft: token('space.600'),
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: token('space.300'),
		justifyContent: 'space-between',
		flexGrow: 1,
		'@media (min-width: 64rem)': {
			width: '50%',
		},
	},
	product: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.400'),
		width: '100%',
	},
	message: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: token('space.200'),
	},
	toggleContainer: {
		display: 'grid',
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		gridTemplateColumns: 'auto 1fr',
		alignItems: 'center',
		gap: token('space.100'),
		borderRadius: token('radius.xsmall'),
		userSelect: 'none',
	},
	toggleContainerEnabled: {
		backgroundColor: token('color.background.accent.lime.subtlest'),
	},
	toggleContainerDisabled: {
		backgroundColor: token('elevation.surface.sunken'),
	},
	label: {
		cursor: 'pointer',
	},
	actions: {
		display: 'flex',
		gap: token('space.050'),
		alignSelf: 'flex-end',
	},
});

export const Content: (props: NormalizeSeatScreenProps) => JSX.Element = ({
	onConfirm,
	testId,
}: NormalizeSeatScreenProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { colorMode } = useThemeObserver();
	const isDarkMode = colorMode === 'dark';
	const {
		orgId,
		redirectUrl,
		isNormalizeSeatsEnabled: isEnabled,
		setIsNormalizeSeatsEnabled,
	} = useModalContext();

	const handleToggleChange = () => {
		const newValue = !isEnabled;
		setIsNormalizeSeatsEnabled(newValue);

		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'twcXflowModalNormalizeSeatToggle',
			source: 'twcXflowModalNormalizeSeatScreen',
			attributes: {
				isEnabled: newValue,
			},
		}).fire('growth');
	};

	return (
		<Box xcss={styles.content}>
			<Box xcss={styles.product}>
				<Image
					src={isDarkMode ? teamworkCollectionHeaderDark : teamworkCollectionHeaderLight}
					srcDark={teamworkCollectionHeaderDark}
					alt={formatMessage(normalizeSeatModalMessages.teamworkCollectionAlt)}
					testId="teamwork-collection-header"
					width="168"
					height="auto"
				/>
				<Box xcss={styles.message}>
					<Heading size="large">
						<FormattedMessage {...normalizeSeatModalMessages.confirmationTitle} />
					</Heading>
					<Text size="medium" color="color.text.subtle">
						<FormattedMessage
							{...normalizeSeatModalMessages.confirmationDescription}
							values={{ link: <AdministrationLink orgId={orgId} /> }}
						/>
					</Text>
				</Box>
				<Box
					xcss={cx(
						styles.toggleContainer,
						isEnabled ? styles.toggleContainerEnabled : styles.toggleContainerDisabled,
					)}
				>
					<Toggle id="normalize-seat-toggle" isChecked={isEnabled} onChange={handleToggleChange} />
					<label htmlFor="normalize-seat-toggle" css={styles.label}>
						<Text weight="medium">
							<FormattedMessage {...normalizeSeatModalMessages.toggleText} />
						</Text>
					</label>
					{!isEnabled && (
						<>
							<Box />
							<Text size="small">
								<FormattedMessage {...normalizeSeatModalMessages.toggleWarning} />
							</Text>
						</>
					)}
				</Box>
			</Box>

			<Box xcss={styles.actions}>
				<LinkButton
					appearance="primary"
					href={redirectUrl}
					onClick={(e) => {
						e.preventDefault();
						onConfirm();
					}}
					testId={testId ? `${testId}-confirm` : undefined}
				>
					<FormattedMessage {...normalizeSeatModalMessages.confirmButton} />
				</LinkButton>
			</Box>
		</Box>
	);
};
